import {put, call} from 'redux-saga/effects';
import Comment from "../../entities/comment";
import * as Actions from "../actions/commentActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CommentAction = {
    commentId: string;
    comment: Comment;
    comments: Comment[];
};

export function* fetchOneComment(action: CommentAction) {
	try {
		const comment: Comment = yield call(api.findOne, 'comment', action.commentId);

		// Now we can update the user interface
		yield put({type: Actions.COMMENT_FETCH_ONE_SUCCESS, commentId: action.commentId, comment});
	}
	catch(err) {
		yield put({type: Actions.COMMENT_FETCH_ONE_FAILURE, commentId: action.commentId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CommentAction} action
 */
export function* saveComment(action: CommentAction) {
    // console.debug('⮂ COMMENT_SAVE saga', action.comment?.id);
    
	if (action.comment === undefined || action.comment === null) {
		console.error('saveComment received a null');
		throw new Error('saveComment received a null');
	}

	if (!(action.comment instanceof Comment)) {
		action.comment = new Comment(action.comment);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'comment', action.comment);
		if (addResult.isErr()) {
			console.error(`⮂ saveComment: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.COMMENT_SAVE_FAILURE, comment: action.comment, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COMMENT_SAVE, comment: action.comment});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'comment', action.comment.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.COMMENT_SAVE_FAILURE, comment: action.comment, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {CommentAction} action
 * @param {Comment[]} action.comments
 */
export function* saveManyComments(action: CommentAction) {
    // console.debug('⮂ COMMENT_SAVE_MANY saga', action.comments.map(comment => comment.id).join(','));
    
    if (!Array.isArray(action.comments)) {
        console.error('saveManyComments expected an array, received', action.comments);
        throw new Error('saveManyComments expected an array');
    }

	try {
		const toSave: Comment[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.comments.length; i++) {
			const comment = action.comments[i];
			comment.persisted = false;
			toSave.push(comment);
		}

		const addResult = yield call(idb.add, 'comment', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyComments: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.COMMENT_SAVE_MANY_FAILURE, comments: action.comments, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COMMENT_SAVE_MANY, comments: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.COMMENT_SAVE_MANY_FAILURE, comments: action.comments, status: 500, err});

		console.error(`Error saving ${action.comments.length} comments to database`, err);
	}
}

/**
 * @param {CommentAction} action
 */
export function* removeComment(action: CommentAction) {
    const userId = getCurrentUserId();
	if (!userId || action.comment === undefined || action.comment === null || !(action.comment instanceof Comment)) {
			console.error('removeComment expected type Comment, received', action.comment);
			throw new Error('removeComment expected type Comment');
		}

	try {
		// Mark as deleted
		action.comment.status = 'D';
		action.comment.addedOn = newHRTimestamp();
		action.comment.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'comment', action.comment);
		if (addResult.isErr()) {
			console.error(`⮂ removeComment: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.COMMENT_REMOVE_FAILURE, comment: action.comment, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COMMENT_REMOVE, comment: action.comment});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.COMMENT_REMOVE_FAILURE, comment: action.comment, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Comment.
	*
	* @param {CommentAction} action
	*/
export function* restoreComment(action: CommentAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreComment expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.comment === undefined || action.comment === null) {
        console.error('restoreComment expected a Comment, received null');
        throw new Error('restoreComment expected a Comment, received null');
    }
    else if (!(action.comment instanceof Comment)) action.comment = new Comment(action.comment); 
    
	try {
		// Mark as restored
		action.comment.status = 'U';
		action.comment.addedOn = newHRTimestamp();
		action.comment.addedBy = userId;
		action.comment.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'comment', action.comment);
		if (addResult.isErr()) {
			console.error(`⮂ restoreComment: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.COMMENT_RESTORE_FAILURE, comment: action.comment, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COMMENT_RESTORE, comment: action.comment});
	}
	catch(err) {
		yield put({type: Actions.COMMENT_RESTORE_FAILURE, comment: action.comment, status: 500, err});

		console.error('Error restoring from database', err);
	}
}